import { Injectable } from '@angular/core';
import { StorageMovementFilter } from '@core/interfaces';
import { TranslateService } from '@ngx-translate/core';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';
import { PieChartItem } from '../../../../core/components/charts/pie/pie.component';

export interface ResultItem {
  key: string;
  value: string;
}

export interface MovementsByType {
  entryCount: number;
  removeCount: number;
  transferCount: number;
}

export interface MovementsByDay {
  date: string;
  entryCount: number;
  removeCount: number;
  transferCount: number;
  finalCount: number;
  productId: string;
  storageId: string;
  productName: string;
  storageName: string;

}

function positionByPeriod(period: string): number {
  const map = {
    'up_to_2_days': 0,
    'between_3_and_6_days': 1,
    'between_7_and_15_days': 2,
    'more_than_15_days': 3,
  };

  return map[period];
}

@Injectable({
  providedIn: 'root'
})
export class DashboardStorageMovementsService {

  constructor(
    private readonly apollo: Apollo,
    private readonly translate: TranslateService
  ) {}
  public async accountStorageMovementsByType(where: StorageMovementFilter): Promise<MovementsByType> {
    const query = gql`
        query accountStorageMovementsByType($where: DashboardStorageWhereInput!) {
          accountStorageMovementsByType(where: $where) {
            reportName
            item {
              entryCount
              removeCount
              transferCount
            }
          }
        }
      `;

    return this.apollo
      .query<any>({
        query,
        variables: { where },
        fetchPolicy: 'network-only'
      })
      .toPromise()
      .then(({ data }) => {
        const item = data.accountStorageMovementsByType.item || {};
        return {
          entryCount: item.entryCount || 0,
          removeCount: item.removeCount || 0,
          transferCount: item.transferCount || 0,
        };
      });
  }

  public async accountProductsByStorage(where: StorageMovementFilter): Promise<PieChartItem[]> {
    const query = gql`
      query accountProductsByStorage($where: DashboardStorageWhereInput!) {
        accountProductsByStorage(where: $where) {
          reportName
          items {
            key
            value
            extra
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        query,
        variables: { where },
        fetchPolicy: 'network-only'
      })
      .toPromise()
      .then(({ data }) => {
        return data.accountProductsByStorage.items.map((item) => {
          return {
            name: item.extra.storage.name,
            value: item.value,
            extra: item.extra,
          };
        });
      });
  }

  public async accountStorageMovementsByDay(where: StorageMovementFilter): Promise<MovementsByDay[]> {
    const query = gql`
      query accountStorageMovementsByDay($where: DashboardStorageWhereInput!) {
        accountStorageMovementsByDay(where: $where) {
          reportName
          items {
            date
            entryCount
            removeCount
            transferCount
            finalCount
            productId
            storageId
            productName
            storageName
          }
        }
      }
    `;

    return this.apollo
      .query<any>({
        query,
        variables: { where },
        fetchPolicy: 'network-only'
      })
      .toPromise()
      .then(({ data }) => {
        return data.accountStorageMovementsByDay.items;
      });
  }

  public async storageMovements(where: StorageMovementFilter, limit: Number, offset: Number): Promise<any> {
    const query = gql`
      query storagesMovements($limit: Int, $offset: Int, $orderBy: StoragesMovementsOrderByInput, $where: StoragesMovementsWhereInput) {
        storagesMovements(limit: $limit, offset: $offset, orderBy: $orderBy, where: $where) {
          pageInfo {
            hasNextPage
            hasPreviousPage
          }
          totalCount
          items {
            id
            description
            type
            product {
              id
              name
              imageUrl
            }
            quantity {
              previous
              changed
              current
            }
            createdAt
            createdBy {
              id
              name
              avatarUrl
            }
            createdByName
            updatedAt
            updatedBy {
              id
              name
            }
            storage {
              id
              name
            }
            externalStorage {
              id
              name
            }
            sync {
              syncedAt
              status
              messageId
            }
            company {
              id
              name
              avatarUrl
            }
            relatedStorageMovement {
              id
              description
              type
              storage {
                id
                name
              }
              product {
                id
                name
              }
              quantity {
                previous
                changed
                current
              }
            }
          }
        }
      }
    `;

    return this.apollo.query<any>({
      query,
      variables: {
        where: {
          createdAtGte: where.from,
          createdAtLte: where.to,
          productId: where.productId,
          storageIdIn: where.storageId_in,
        },
        limit,
        offset,
        orderBy: 'createdAt_desc'
      },
      fetchPolicy: 'network-only'
    })
      .toPromise()
      .then(({ data }) => {
        return data.storagesMovements.items;
      });
  }
}

